import { SHORT } from './snackbarDuration';
import { IAppState } from '../core/redux/IAppState';
import { Action } from 'redux';
import { ACTION_TYPE } from '../core/redux/actionType';
import { sortByProp, assoc } from '../language/module';
import { Snackbar } from './snackbar';


export type ShowSnackbarAction = {
  type: ACTION_TYPE.SHOW_SNACKBAR,
  snackbar: Snackbar,
};


export type HideSnackbarAction = {
  type: ACTION_TYPE.HIDE_SNACKBAR,
  snackbar: Snackbar,
};


/**
 * Creates an action you can dispatch to change the app state to 'showing a Snackbar with the given details'.
 * @param message - The message to show
 * @param isError - Whether or not this is an error (default: false)
 * @param timeoutMs - How long to show the message for in milliseconds (default: 5000ms)
 * @param priority - The Snackbar's priority (higher number is greater priority)
 */
export const showSnackbarAction = (
  message: string,
  isError: boolean  = false,
  timeoutMs: number = SHORT,
  priority: number  = 0,
): ShowSnackbarAction => ({
  type:  ACTION_TYPE.SHOW_SNACKBAR,
  snackbar: { message, isError, timeoutMs, priority },
});


/**
 * Makes an action that can be dispatched to hide any Snackbars. Pass the Snackbar you want to hide (uses ref equality to
 * remove)
 */
export const hideSnackbarAction = (snackbar: Snackbar): HideSnackbarAction => ({
  type:  ACTION_TYPE.HIDE_SNACKBAR,
  snackbar,
});


/**
 * Function mapping ShowSnackbar actions into new app state. Snackbars are those little messages that pop up along the bottom
 * of the page, then go away after a few seconds.
 */
export const snackbarReducer = (state: IAppState, action: Action): IAppState => {
  if (action.type === ACTION_TYPE.HIDE_SNACKBAR) {
    return assoc(
      'snackbars',
      state.snackbars.filter(snackbar => snackbar !== (action as HideSnackbarAction).snackbar),
      state,
    );
  }

  if (action.type === ACTION_TYPE.SHOW_SNACKBAR) {
    return assoc(
      'snackbars',
      // Sort Snackbars by priority, highest first, ignore case (faster)
      sortByProp('priority', true, false, state.snackbars.concat((action as ShowSnackbarAction).snackbar)),
      state,
    );
  }

  return state;
};
